/**
 * UninstallPage — the page Chrome opens when the extension is removed
 * (`chrome.runtime.setUninstallURL` → `app.reflecto.dev/uninstall`).
 *
 * The visitor has already left. The page's only job is to close the loop
 * honestly — say the removal is complete and that nothing of theirs is
 * left anywhere — then leave one question open in case they feel like
 * answering it. Closing the tab immediately must read as a normal
 * outcome, not an abandoned funnel.
 *
 * Three shape decisions from the design handoff, all deliberate:
 *
 *   - **No app chrome.** No `AppShell`, no `HeaderBar`, no `Surface`.
 *     This visitor has no session and no paired device, so app furniture
 *     would imply they are still inside a product they just removed. The
 *     page reads as a document: one column on `--bg-primary`, and the
 *     only borders are the two fields and the footer rule.
 *   - **Submit is `variant="secondary"`.** `primary` paints
 *     `--brand-gradient` (cyan→green), and green means "connected" /
 *     "encrypted" everywhere else in the product. A celebratory gradient
 *     CTA on a churn page is the wrong register. Secondary is the
 *     loudest thing here, which is enough — it is the only control.
 *   - **Success is typographic.** No green, no checkmark, no second ask.
 *     One line of `--text-primary` where the ask used to be.
 *
 * Do not add a rating prompt, a reinstall CTA, an analytics beacon, or an
 * illustration. Each is an exclusion, not an oversight — in particular
 * there is no churn event, because a tracking beacon on a page whose
 * whole argument is "we are not tracking you" contradicts the copy. The
 * stores already report uninstall volume.
 *
 * SECURITY: the message and email are user content. They go to
 * `submitFeedback` and nowhere else — never logged. No device id is
 * attached (see `submitFeedback`'s `deviceId` note): the privacy note
 * promises this form "is not linked to any device", and a paired visitor
 * landing here must not falsify that.
 */

import type { FeedbackCategory } from "@reflecto/client-ui";
import { Button, Icon, Input, Link, Logo, Tab, TabGroup, Text } from "@reflecto/client-ui";
import { AlertTriangle, Clock } from "@untitledui/icons";
import { type FormEvent, useCallback, useId, useLayoutEffect, useRef, useState } from "react";
import { messages } from "../i18n";
import { FeedbackSubmitError, submitFeedback } from "../services/feedback";
import styles from "./UninstallPage.module.css";

/** Mirrors the server's `MAX_FEEDBACK_MESSAGE`. */
const MESSAGE_MAX = 4096;
/**
 * The counter appears only in the last stretch before the cap. Below
 * this there is nothing to render — a permanent counter would be one
 * more thing on a page that is trying to ask exactly one question.
 */
const COUNTER_THRESHOLD = MESSAGE_MAX - 200;

/**
 * Footer link target — the app root, NOT an absolute production URL.
 *
 * This page IS the web app (Chrome opens it at
 * `app.reflecto.dev/uninstall`), so "Open the web app" only ever has
 * to go to the root of whatever origin is already serving it.
 * Hardcoding `https://app.reflecto.dev` would send a PR-preview visitor
 * (`/pr-1161/`) and a localhost dev visitor to production instead of the
 * app they are looking at.
 *
 * `import.meta.env.BASE_URL` is Vite's build-time-resolved `base` and
 * always ends in `/` — the same seam `ShareTargetPage`'s `returnToApp()`
 * and `router.ts`'s `isShareTargetPath` already use.
 */
const WEB_APP_URL = import.meta.env.BASE_URL;

type Status = "editing" | "sending" | "success" | "error";
/** Which alert the error state shows. 429 gets its own icon and copy. */
type ErrorKind = "generic" | "rateLimited";

const m = messages.uninstall;

export function UninstallPage() {
  const [category, setCategory] = useState<FeedbackCategory>("other");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [status, setStatus] = useState<Status>("editing");
  const [errorKind, setErrorKind] = useState<ErrorKind | null>(null);
  /** Set when a submit is attempted with an empty message (the no-JS / Enter-key edge). */
  const [showRequired, setShowRequired] = useState(false);

  const messageId = useId();
  const requiredId = useId();
  const successRef = useRef<HTMLDivElement>(null);

  const sending = status === "sending";
  const canSubmit = message.trim().length > 0 && !sending;

  // The submit button is gone in the success state, so a keyboard or
  // screen-reader user would otherwise be left on a control that no
  // longer exists. Move focus to the thank-you line instead.
  //
  // `useLayoutEffect` is load-bearing here — do not "tidy" it back to
  // `useEffect`. `setStatus("success")` is set from an async continuation
  // in `handleSubmit`, so React runs on its real scheduler and a passive
  // effect is flushed on a `setImmediate` (Node's check phase). That
  // races the test runner's own drain (`setTimeout 0`, timers phase),
  // and `waitFor` on the success text gates on the commit, not on the
  // effect. A layout effect runs inside the commit, so focus lands with
  // the DOM change instead of one frame later. That is both the product
  // behaviour a keyboard user needs and the fix for a real CI flake
  // (#1507) — reverting reintroduces an intermittent failure, not a
  // clean one.
  useLayoutEffect(() => {
    if (status === "success") successRef.current?.focus();
  }, [status]);

  const handleMessageChange = useCallback(
    (value: string) => {
      setMessage(value);
      setShowRequired(false);
      // Clear the alert the moment they edit, so it never lingers over
      // text they have already changed (mirrors FeedbackModal).
      if (status === "error") {
        setStatus("editing");
        setErrorKind(null);
      }
    },
    [status]
  );

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (sending) return;
      const trimmed = message.trim();
      if (trimmed.length === 0) {
        setShowRequired(true);
        return;
      }
      setStatus("sending");
      setErrorKind(null);
      try {
        await submitFeedback(
          {
            category,
            message: trimmed,
            email: email.trim() === "" ? undefined : email.trim(),
            website,
          },
          { surface: "uninstall" }
        );
      } catch (err) {
        setErrorKind(
          err instanceof FeedbackSubmitError && err.rateLimited ? "rateLimited" : "generic"
        );
        setStatus("error");
        return;
      }
      setStatus("success");
    },
    [category, email, message, sending, website]
  );

  const submitLabel = sending ? m.sending : status === "error" ? m.retry : m.submit;
  const charactersLeft = message.length > COUNTER_THRESHOLD ? MESSAGE_MAX - message.length : null;

  return (
    <div className={styles.page} data-route="uninstall">
      <div className={styles.column}>
        {/* 22px is the drawn wordmark height and sits between Logo's `sm`
         * (20) and `md` (24) buckets, so the height comes from the module. */}
        <Logo className={styles.logo} decorative />

        <Text as="h1" size="xxl" weight="semibold" className={styles.headline}>
          {m.headline}
        </Text>
        <Text as="p" size="m" tone="secondary" className={styles.body}>
          {m.body}
        </Text>

        {status === "success" ? (
          <div
            ref={successRef}
            className={styles.success}
            role="status"
            aria-live="polite"
            tabIndex={-1}
          >
            <Text as="p" size="m" weight="medium">
              {m.success}
            </Text>
          </div>
        ) : (
          <>
            <Text as="p" size="m" weight="medium" className={styles.ask}>
              {m.ask}
            </Text>

            {/* Native constraint validation is deliberately LEFT ON (no
             * `noValidate`). The email field is optional, so an empty one is
             * already valid and nothing fires for the common case — but a
             * typo'd address would otherwise sail through to the server, come
             * back a 400, and surface as "That did not send", losing a message
             * the visitor is never coming back to retype. The browser blocking
             * the submit and pointing at the field is both free and honest.
             * The design draws no invalid-email state for exactly this reason.
             * Note the textarea is NOT `required` — its empty case is the
             * page's own `messageRequired` copy, not a native bubble. */}
            <form className={styles.form} onSubmit={handleSubmit}>
              <fieldset className={styles.fieldset} disabled={sending}>
                <legend className={styles.legend}>{m.categoryLegend}</legend>
                <TabGroup
                  className={styles.tabs}
                  value={category}
                  onChange={(v) => setCategory(v as FeedbackCategory)}
                  aria-label={m.categoryLegend}
                  disabled={sending}
                >
                  <Tab value="bug">{m.categoryBug}</Tab>
                  <Tab value="idea">{m.categoryIdea}</Tab>
                  <Tab value="other">{m.categoryOther}</Tab>
                </TabGroup>
              </fieldset>

              <div className={styles.field}>
                {/* Native label + textarea: there is no Textarea primitive, and
                 * one page is not enough to justify promoting one. The rules in
                 * the module are lifted from FeedbackModal.module.css. */}
                <label className={styles.label} htmlFor={messageId}>
                  {m.messageLabel}
                </label>
                <textarea
                  id={messageId}
                  className={styles.textarea}
                  value={message}
                  onChange={(e) => handleMessageChange(e.target.value)}
                  placeholder={m.messagePlaceholder}
                  maxLength={MESSAGE_MAX}
                  disabled={sending}
                  aria-invalid={showRequired || undefined}
                  aria-describedby={showRequired ? requiredId : undefined}
                />
                {showRequired && (
                  <Text as="p" size="xs" tone="negative" id={requiredId} role="alert">
                    {m.messageRequired}
                  </Text>
                )}
                {charactersLeft !== null && (
                  <Text as="p" size="xs" tone="tertiary" className={styles.counter}>
                    {m.charactersLeft(charactersLeft)}
                  </Text>
                )}
              </div>

              <Input
                label={m.emailLabel}
                size="sm"
                type="email"
                inputMode="email"
                autoComplete="email"
                maxLength={254}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={m.emailPlaceholder}
                helpText={m.emailHelp}
                disabled={sending}
              />

              {/* Honeypot. Stays in the DOM and in the payload: the server drops
               * filled-honeypot submissions BEFORE rate limiting, so bots cannot
               * exhaust the surface quota — dropping this field removes a real
               * defence. Clipped, never `display: none` alone. */}
              <input
                className={styles.honeypot}
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />

              {errorKind !== null && (
                <div className={styles.alert} role="alert">
                  <Icon
                    as={errorKind === "rateLimited" ? Clock : AlertTriangle}
                    size="sm"
                    tone="negative"
                    className={styles.alertIcon}
                  />
                  <Text as="span" size="s">
                    {errorKind === "rateLimited" ? m.errorRateLimited : m.errorGeneric}
                  </Text>
                </div>
              )}

              <div className={styles.submitRow}>
                <Button
                  variant="secondary"
                  size="m"
                  type="submit"
                  loading={sending}
                  disabled={!canSubmit}
                >
                  {submitLabel}
                </Button>
              </div>

              <Text as="p" size="xs" tone="tertiary" className={styles.privacyNote}>
                {m.privacyNote}
              </Text>
            </form>
          </>
        )}

        <div className={styles.footer}>
          <Text as="span" size="xs" tone="tertiary">
            {m.footerWebApp}
          </Text>
          <Link href={WEB_APP_URL} size="xs">
            {m.footerWebAppLink}
          </Link>
        </div>
      </div>
    </div>
  );
}
